import { Navigate, Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppBar,
  Container,
  Stack,
  Toolbar,
  Link as MuiLink,
  LinearProgress,
  Box,
  Divider,
  useTheme,
  Button,
  Typography,
} from '@mui/material';
import Logo from '~/assets/images/logo-black.svg?react';
import { useProfileToken } from '../components/profile/hooks/profile.hooks';
import { isEmpty } from 'lodash';
import { Footer } from '~/modules/storefront/layouts/components';
import routes from '~/modules/storefront/router/routes.ts';
import { SessionContextProvider, useSessionContext } from '~/contexts/session.context.tsx';
import { useGetUserQuery } from '~/services/user.ts';
import { TokenExpiredMessage } from '../components/common/TokenExpiredMessage';
import { LocationProvider } from '~/contexts/location.context';
import { Logout } from '@mui/icons-material';
import { getConfig } from '~/services/config';
import { getQueryClient } from '~/services/query-client';
import { PendoPageTracker } from '~/modules/@shared/components';

const MainLayout = () => {
  const { clearToken } = getConfig();
  const theme = useTheme();
  const { isAuth } = useSessionContext();
  const { data: user, isLoading } = useGetUserQuery();
  const navigate = useNavigate();

  const location = user?.locations[0];
  const locationLogo = location?.logo_url;

  if (isLoading) return <LinearProgress />;

  if (!isAuth) {
    return <Navigate to={routes.auth.login} replace />;
  }

  if (!location) return null;

  const handleLogout = () => {
    navigate(routes.auth.login);

    setTimeout(() => {
      clearToken();
      const queryClient = getQueryClient();
      queryClient.resetQueries();
    }, 50);
  };

  return (
    <LocationProvider defaultLocation={location}>
      <Stack
        sx={{
          background: 'background.default',
          minHeight: '100vh',
        }}>
        <AppBar
          position="sticky"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: theme.palette.background.default,
            height: 100,
            justifyContent: 'center',
          }}>
          <Toolbar>
            <MuiLink onClick={() => window.location.reload()} sx={{ width: 250, display: 'flex', flex: 1 }}>
              <Logo style={{ marginRight: '10px', width: 95, height: 40 }} />
              <Divider orientation="vertical" flexItem sx={{ borderColor: theme.palette.text.secondary, marginX: 1 }} />
              {!isEmpty(locationLogo) && <img style={{ maxHeight: 45 }} src={locationLogo} alt="gym logo" />}
            </MuiLink>
            <Button color="secondary" onClick={handleLogout}>
              <Logout />
              <Typography sx={{ marginLeft: 1 }}>Logout</Typography>
            </Button>
          </Toolbar>
        </AppBar>
        <Container
          component="main"
          maxWidth="md"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            paddingX: { xs: 2, sm: 8 },
            borderRight: '1px solid #DEDEDE',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '100vh',
            maxHeight: '100vh',
          }}>
          <Box sx={{ paddingTop: { xs: 5 } }}>
            <Outlet />
          </Box>
          <Box sx={{ marginBottom: { xs: 5, md: 16 }, width: '100%' }}>
            <Divider flexItem sx={{ marginBottom: 5 }} />
            <Footer
              gym_address={
                location ? `${location?.address_1} ${location?.address_2}, ${location?.city} ${location?.zip}` : ''
              }
              gym_phone_number={location?.cs_phone}
            />
          </Box>
        </Container>
        
        {/* Pendo page tracking for authenticated users */}
        <PendoPageTracker />
      </Stack>
    </LocationProvider>
  );
};

function MainLayoutWrapper() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { data: userStatus, isLoading } = useProfileToken(token);

  if (isLoading) return null;

  if (userStatus?.status === 'INVALID_TOKEN') {
    return <TokenExpiredMessage />;
  }

  if (token && userStatus?.data.active) {
    return <Navigate to={routes.auth.login} replace />;
  }

  if (token && userStatus?.data.active === false) {
    return <Navigate to={`${routes.auth.create_password}?token=${token}`} replace />;
  }

  return (
    <SessionContextProvider>
      <MainLayout />
    </SessionContextProvider>
  );
}

export default MainLayoutWrapper;
